import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Grid, Modal, Menu, MenuItem, CircularProgress, Backdrop, Paper, Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { BASEURL } from '../../constants/Common';
import FloatingButton from '../usersupport/helpdesk/FloatingButton';
import getTheme from '../../theme/theme';
import { toast } from 'react-toastify';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';

export interface SubscriptionPlan {
  title: string;
  price: number;
  projects: number;
  users: number;
}

export interface Organization {
  subscription_start_date: string;
  subscription_end_date: string;
  active_users_count: number;
  active_project_count: number;
  organization_subscription_status: string;
  subscription_id: number;
  organization_name: string;
}

export interface OrganizationFinal {
  organization: Organization;
  subscription_plan: SubscriptionPlan;
}

const SubscriptionMainPage: React.FC = () => {
  const [organizationFinal, setOrganizationFinal] = useState<OrganizationFinal | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const userId = useSelector((state: RootState) => state.user.userId);
  const email = localStorage.getItem('email');
  const storedAdmindata = localStorage.getItem('userRoleAdmin');
  let UserroleAdmin: any = null;
  if (storedAdmindata) {
    UserroleAdmin = JSON.parse(storedAdmindata);
  } else {
    console.log('No data found in localStorage');
  }
  const [userRole, setUserRole] = useState<string>(UserroleAdmin);
  const isUserAdmin = userRole === 'Admin';

  const storedData = localStorage.getItem('orgid');
  let parsedData: any;

  if (storedData) {
    parsedData = JSON.parse(storedData);
  } else {
    navigate('/login');
  }

  const organizationId = parsedData;

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await axios.get(`${BASEURL}/organization/${organizationId}/`, {
          headers: { Accept: 'application/json' },
        });

        const { subscription_end_date, organization_subscription_status, organization_name } = response.data.organization;
        const currentDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format

        let updatedStatus = organization_subscription_status;

        if (subscription_end_date < currentDate) {
          updatedStatus = 'Expired';
          await axios.put(`${BASEURL}/organizations/update/${organizationId}/`, {
            organization_name,
            organization_subscription_status: updatedStatus,
          }, {
            headers: { 'Content-Type': 'application/json' },
          });
        }

        // Set the updated organization data
        setOrganizationFinal((prevState) => ({
          ...prevState,
          ...response.data,
          organization: {
            ...response.data.organization,
            organization_subscription_status: updatedStatus,
          },
        }));
      } catch (error) {
        console.error('Error fetching organization:', error);
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      setLoading(true);
      fetchOrganization();
    }
  }, [organizationId]);

  const viewPlan = () => {
    navigate('/subscriptionplans', { state: { data: organizationFinal } });
  };
  const ViewHistory = () => {
    navigate('/subscriptionhistory');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCancelSubscription = () => {
    // Check if it's a free trial and disable cancel option
    if (organizationFinal?.subscription_plan?.title === 'Premium Plus - Free trial') {
      return;
    }
    setAnchorEl(null);
    setShowConfirmationModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false); // Close the modal
    setLoading(true); // Set loading state to true

    axios
      .post(`${BASEURL}/cancel-subscription/`, { organization_id: organizationId, email }, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        if (response.status === 200) {
          // After cancellation, fetch the updated organization details
          axios
            .get(`${BASEURL}/organization/${organizationId}/`, {
              headers: {
                Accept: 'application/json',
              },
            })
            .then((response) => {
              setOrganizationFinal(response.data);
              setLoading(false);
              toast.success('Your subscription is cancelled successfully');
              navigate('/subscriptions', { state: { data: response.data } });
            })
            .catch((error) => {
              setLoading(false);
              console.error('Error fetching organization details after cancellation:', error)
            });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error('Error cancelling subscription:', error);
      });
  };

  return (
    <div>
      <ThemeProvider theme={getTheme()}>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: '-60px' }}>
          <Box sx={{ flex: 'auto', overflow: 'auto' }}>
            <Typography variant="h5" component="div">
              Subscriptions
            </Typography>
          </Box>
          <Box sx={{ marginLeft: 'auto' }}>
            <Button variant="contained" color="primary" sx={{ mr: 1 }} onClick={viewPlan}>
              View Plans/Renew
            </Button>
            <Button variant="contained" color="primary" sx={{ mr: 1 }} onClick={ViewHistory} disabled = {!isUserAdmin}>
              History
            </Button>
            <Button
              disabled={organizationFinal?.subscription_plan?.title === 'Premium Plus - Free trial' || !isUserAdmin || organizationFinal?.organization?.organization_subscription_status !== 'Active'} // Disable during free trial and user is not admin
              sx={{
                color: 'black',
                minWidth: 'auto',
                borderRadius: 0,
                fontSize: '1.5rem',
              }}
              onClick={handleMenuOpen}
            >
              ⋮
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleCancelSubscription}>Cancel Subscription</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={8} sx={{ ml: '100px', mt: '-20px' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: '50px' }}>
                <Backdrop
                  sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                  open={loading}
                >
                  <CircularProgress color='inherit' />
                </Backdrop>
              </Box>
            ) : (
              organizationFinal?.organization?.organization_subscription_status === 'Expired' ? (
                <Typography variant="body1">
                  <h3>Please purchase a subscription plan.</h3>
                </Typography>
              ) : organizationFinal?.organization?.subscription_id ? (
                <>
                  <h3>Current Plan:</h3>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">Plan Name:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        {organizationFinal.subscription_plan.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">Status:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">
                        {organizationFinal.organization.organization_subscription_status}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">Description:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">
                        Number of users:{' '}
                        {organizationFinal.subscription_plan.users}
                      </Typography>
                      <Typography variant="subtitle1">
                        Number of projects:{' '}
                        {organizationFinal.subscription_plan.projects}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">Plan Start Date:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">
                        {organizationFinal.organization.subscription_start_date}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">Plan End Date:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">
                        {organizationFinal.organization.subscription_end_date}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">Cost:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ mt: '10px' }}>
                      <Typography variant="subtitle1">
                        USD {organizationFinal.subscription_plan.price}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={4} sx={{ mt: '20px' }}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <PeopleIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle2">
                              Active Users Count
                            </Typography>
                            <Typography variant="h6">
                              {organizationFinal.organization.active_users_count}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <FolderIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle2">
                              Active Projects Count
                            </Typography>
                            <Typography variant="h6">
                              {organizationFinal.organization.active_project_count}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body1">
                  <h3>Please purchase a subscription plan.</h3>
                </Typography>
              )
            )}
          </Grid>
        </Grid>
      </ThemeProvider>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmationModal}
        onClose={handleCloseModal}
        aria-labelledby="cancel-subscription-modal"
        aria-describedby="confirm-cancel-subscription"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="confirm-cancel-subscription" sx={{ mt: 2 }}>
            <p>Dear User,</p>
            <p>
              Are you sure you want to cancel your subscription? You will lose
              access to creating users, projects, modules, and reports.
            </p>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button variant="contained" onClick={handleCancel}>
              Yes, Cancel
            </Button>
            <Button variant="contained" onClick={handleCloseModal}>
              No, Keep Subscription
            </Button>
          </Box>
        </Box>
      </Modal>

      <FloatingButton />
    </div >
  );
};

export default SubscriptionMainPage;



